import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePlanBuilder } from '../context/PlanBuilderContext';
import Button from '../../../../../ui/canon/Button';
import { SparklesIcon } from 'lucide-react';
import YesNoWidget from './YesNoWidget';
import TrueFalseWidget from './TrueFalseWidget';
import MultipleChoiceWidget from './MultipleChoiceWidget';
import TextInputWidget from './TextInputWidget';
import NumberInputWidget from './NumberInputWidget';
import CurrencySliderWidget from './CurrencySliderWidget';
import DatePickerWidget from './DatePickerWidget';
import RatingWidget from './RatingWidget';
import PercentageSliderWidget from './PercentageSliderWidget';
import FileUploadWidget from './FileUploadWidget';
import RankingWidget from './RankingWidget';
import ChecklistWidget from './ChecklistWidget';
import ContactWidget from './ContactWidget';
import TimelineWidget from './TimelineWidget';
import ComparisonWidget from './ComparisonWidget';
import EmailWidget from './EmailWidget';
import TelWidget from './TelWidget';
import UrlWidget from './UrlWidget';
import RepeaterWidget from './RepeaterWidget';
import AiSuggestionCard from './AiSuggestionCard';

// Widget types that support text-based AI suggestion injection
const TEXT_BASED_WIDGETS = ['text', 'textarea', 'email', 'url', 'tel'];
// Widget types that support AI suggestions
const AI_SUGGESTION_ELIGIBLE_WIDGETS = [
  'text', 'textarea', 'yesno', 'truefalse', 'multiple-choice',
  'number', 'currency', 'percentage', 'date', 'rating', 'checklist'
];

const QuestionRenderer = ({
  question,
  onAnswer,
  answer,
  disabled,
  initialValue = null
}) => {
  const intl = useIntl();

  // Get injected answer and inline suggestion from context
  const {
    injectedAnswer,
    clearInjectedAnswer,
    inlineSuggestion,
    inlineSuggestionLoading,
    inlineSuggestionError,
    fetchInlineSuggestion,
    useInlineSuggestion,
    dismissInlineSuggestion,
    planBuilderAgentChatEnabled
  } = usePlanBuilder();

  // Track if we've consumed the injected answer for this render
  const [consumedInjection, setConsumedInjection] = useState(null);

  // When injectedAnswer changes and is relevant to supported widgets, use it
  useEffect(() => {
    if (injectedAnswer && TEXT_BASED_WIDGETS.includes(question?.widgetType)) {
      setConsumedInjection(injectedAnswer);
      clearInjectedAnswer();
    }
  }, [injectedAnswer, question?.widgetType, clearInjectedAnswer]);

  // Clear consumed injection when question changes
  useEffect(() => {
    setConsumedInjection(null);
  }, [question?.id]);

  if (!question) return null;

  // Destructure using camelCase keys as returned by GraphQL
  const {
    id: questionId,
    widgetType,
    options,
    placeholder,
    validationRules,
    aiSuggestionEnabled,
    aiPrompt,
    required,
    helpText
  } = question;

  // Extract maxLength from validationRules or options, default to 1000 for text inputs
  const getMaxLength = () => {
    if (validationRules?.maxLength) return validationRules.maxLength;
    if (options?.maxLength) return options.maxLength;
    return 1000; // Default max length for text inputs
  };

  // Extract the value from initialValue (which may be { value: X, formatted: Y })
  const extractedValue = initialValue?.value !== undefined ? initialValue.value : initialValue;

  // Use consumed injection if available, otherwise use extractedValue
  const effectiveInitialValue = consumedInjection !== null ? consumedInjection : extractedValue;

  // Check if AI suggestion is available and applicable
  // Requires: company has plan_builder_agent_chat feature, question has aiSuggestionEnabled, and widget type is eligible
  const canShowAiSuggestion = planBuilderAgentChatEnabled && aiSuggestionEnabled && AI_SUGGESTION_ELIGIBLE_WIDGETS.includes(widgetType);
  const hasActiveSuggestion = inlineSuggestion?.questionId === questionId;
  const showSuggestionCard = hasActiveSuggestion || inlineSuggestionLoading || inlineSuggestionError;

  // Handle "Help me with this answer" click
  const handleHelpClick = () => {
    fetchInlineSuggestion(questionId);
  };

  // Handle using the suggestion
  const handleUseSuggestion = (suggestion) => {
    useInlineSuggestion(suggestion);
    dismissInlineSuggestion();
  };

  // Render the AI suggestion section
  const renderAiSuggestionSection = () => {
    if (!canShowAiSuggestion) return null;

    return (
      <div className="space-y-3 mb-4">
        {/* Help button - show only when no active suggestion/loading */}
        {!showSuggestionCard && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleHelpClick}
            disabled={disabled}
            className="gap-2 text-succession-primary hover:text-succession-primary-dark"
          >
            <SparklesIcon className="h-4 w-4" />
            {intl.formatMessage({ id: 'succession.plans.widget_help_me_with_answer' })}
          </Button>
        )}

        {/* Suggestion card */}
        {showSuggestionCard && (
          <AiSuggestionCard
            suggestion={inlineSuggestion?.suggestion}
            reasoning={inlineSuggestion?.reasoning}
            confidence={inlineSuggestion?.confidence}
            isLoading={inlineSuggestionLoading}
            error={inlineSuggestionError}
            onUseSuggestion={handleUseSuggestion}
            onDismiss={dismissInlineSuggestion}
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  // Wrap widget with AI suggestion section
  const renderWidgetWithSuggestion = (widget) => (
    <div className="space-y-4">
      {renderAiSuggestionSection()}
      {widget}
    </div>
  );

  switch (widgetType) {
    case 'yesno':
      return renderWidgetWithSuggestion(
        <YesNoWidget
          onAnswer={onAnswer}
          disabled={disabled}
          initialValue={extractedValue}
        />
      );
    case 'truefalse':
      return renderWidgetWithSuggestion(
        <TrueFalseWidget
          onAnswer={onAnswer}
          disabled={disabled}
          initialValue={extractedValue}
        />
      );
    case 'multiple-choice':
      return renderWidgetWithSuggestion(
        <MultipleChoiceWidget
          options={options?.options || []}
          onAnswer={onAnswer}
          disabled={disabled}
          allowMultiple={options?.allowMultiple}
          initialValue={extractedValue}
        />
      );
    case 'text':
    case 'textarea':
      return renderWidgetWithSuggestion(
        <TextInputWidget
          onAnswer={onAnswer}
          disabled={disabled}
          placeholder={placeholder}
          multiline={widgetType === 'textarea'}
          initialValue={effectiveInitialValue}
          required={required}
          maxLength={getMaxLength()}
          helpText={helpText}
          key={consumedInjection ? `injected-${Date.now()}` : 'default'}
        />
      );
    case 'email':
      return (
        <EmailWidget
          onAnswer={onAnswer}
          disabled={disabled}
          placeholder={placeholder || 'Enter email address...'}
          initialValue={effectiveInitialValue}
          key={consumedInjection ? `injected-${Date.now()}` : 'default'}
        />
      );
    case 'number':
      return renderWidgetWithSuggestion(
        <NumberInputWidget
          onAnswer={onAnswer}
          disabled={disabled}
          placeholder={placeholder}
          suffix={options?.suffix}
          initialValue={extractedValue}
        />
      );
    case 'currency':
      return renderWidgetWithSuggestion(
        <CurrencySliderWidget
          onAnswer={onAnswer}
          disabled={disabled}
          min={options?.min || 0}
          max={options?.max || 10000000}
          step={options?.step || 100000}
          defaultValue={options?.defaultValue || undefined}
          label={options?.label || "Estimated Value"}
          initialValue={extractedValue}
        />
      );
    case 'percentage':
      return renderWidgetWithSuggestion(
        <PercentageSliderWidget
          onAnswer={onAnswer}
          disabled={disabled}
          min={options?.min}
          max={options?.max}
          step={options?.step}
          defaultValue={options?.defaultValue}
          initialValue={extractedValue}
        />
      );
    case 'date':
      return renderWidgetWithSuggestion(
        <DatePickerWidget
          onAnswer={onAnswer}
          disabled={disabled}
          initialValue={extractedValue}
        />
      );
    case 'rating':
      return renderWidgetWithSuggestion(
        <RatingWidget
          onAnswer={onAnswer}
          disabled={disabled}
          max={options?.max || 5}
          initialValue={extractedValue}
        />
      );
    case 'file':
      return (
        <FileUploadWidget
          onAnswer={onAnswer}
          disabled={disabled}
          accept={options?.accept}
        />
      );
    case 'ranking':
      return (
        <RankingWidget
          onAnswer={onAnswer}
          disabled={disabled}
          options={options?.options || []}
          initialValue={extractedValue}
        />
      );
    case 'checklist':
      return renderWidgetWithSuggestion(
        <ChecklistWidget
          onAnswer={onAnswer}
          disabled={disabled}
          options={options?.options || []}
          initialValue={extractedValue}
        />
      );
    case 'contact':
      return (
        <ContactWidget
          onAnswer={onAnswer}
          disabled={disabled}
          initialValue={extractedValue}
        />
      );
    case 'timeline':
      return (
        <TimelineWidget
          onAnswer={onAnswer}
          disabled={disabled}
          initialValue={extractedValue}
        />
      );
    case 'comparison':
      return (
        <ComparisonWidget
          onAnswer={onAnswer}
          disabled={disabled}
          options={options?.options || []}
          initialValue={extractedValue}
        />
      );
    case 'tel':
      return (
        <TelWidget
          onAnswer={onAnswer}
          disabled={disabled}
          placeholder={placeholder || 'Enter phone number...'}
          initialValue={extractedValue}
        />
      );
    case 'url':
      return (
        <UrlWidget
          onAnswer={onAnswer}
          disabled={disabled}
          placeholder={placeholder || 'Enter URL...'}
          initialValue={extractedValue}
        />
      );
    case 'repeater':
      return (
        <RepeaterWidget
          onAnswer={onAnswer}
          disabled={disabled}
          options={options || {}}
          initialValue={extractedValue}
        />
      );
    default:
      return (
        <div className="text-red-500">
          Unknown widget type: {widgetType}
        </div>
      );
  }
};

export default QuestionRenderer;
